import { z } from 'zod';

/**
 * Lo que la caseta le cuenta a la nube sobre sí misma.
 *
 * ## Por qué una foto y no un flujo de eventos
 *
 * El estado de un aparato no es «pasó algo», es «así está ahora». Si un envío
 * se pierde, el siguiente lo corrige solo: no hace falta bandeja de salida, ni
 * acuses, ni reintentos. Eso lo separa de los accesos, que sí son eventos —
 * ocurrieron una vez y si el POST se pierde no hay forma de reconstruirlos—.
 *
 * Y por eso tampoco se manda cada latido: llegan cada pocos segundos por
 * aparato, y lo que importa no es el latido sino la conclusión.
 */

/** Cada cuánto reporta la caseta. */
export const REPORTE_INTERVALO_MS = 60_000;

/**
 * Cuánto puede tardar un reporte en llegar antes de descartarse.
 *
 * Sin esto, quien capture un reporte de «todo bien» podría reproducirlo más
 * tarde para tapar una caída. Cinco minutos es holgado para un reloj mal
 * puesto y corto para que la reproducción sirva de algo.
 */
export const REPORTE_VENTANA_MS = 5 * 60_000;

/**
 * A partir de cuándo el último reporte deja de contar como reciente.
 *
 * Tres intervalos: uno perdido no dice nada —la red hace eso— y tres seguidos
 * ya son casi tres minutos de silencio, que sí lo dice. Pasado esto, la nube
 * no enseña el estado de los aparatos como si fuera de ahora, porque no lo es.
 */
export const REPORTE_FRESCO_MS = 3 * REPORTE_INTERVALO_MS;

export const reporteEstadoSchema = z.object({
  /** El slug, no el id: es lo que la caseta conoce sin consultar nada. */
  condominio: z.string().min(1).max(60),

  /**
   * Cuándo miró la caseta, con **su** reloj.
   *
   * Se manda y no se deduce de la hora de llegada porque son cosas distintas:
   * un reporte puede tardar en el camino, y confundir «cuándo lo vi» con
   * «cuándo llegó» convierte una red lenta en aparatos caídos.
   */
  observadoEn: z.string().datetime(),

  aparatos: z
    .array(
      z.object({
        /** El del tópico MQTT, que es el que la caseta tiene a mano. */
        clienteId: z.string().min(1).max(80),
        /** Cuándo latió por última vez, o null si nunca lo ha hecho. */
        ultimoLatido: z.string().datetime().nullable(),
      }),
    )
    .max(500),
});

export type ReporteEstado = z.infer<typeof reporteEstadoSchema>;

/** Cabecera con el identificador de la llave que firma. */
export const CABECERA_KID = 'x-kodica-kid';

/** Cabecera con la firma Ed25519 del cuerpo crudo, en base64url. */
export const CABECERA_FIRMA = 'x-kodica-firma';

// ── El buzón de fotos ───────────────────────────────────────────────
//
// La cara de quien entró tiene que llegar al residente, y el residente puede
// estar en cualquier parte. Su teléfono **no alcanza la caseta** —no hay puerto
// abierto hacia dentro del fraccionamiento—, así que la imagen pasa por la
// nube. Lo que se decide no es si pasa, sino cuánto se queda.
//
// Se queda poco. Arriba es un buzón: llega, el residente la ve, se borra el
// archivo y queda la fila. El archivo permanente vive abajo, que es de quien es.

/** Cada cuánto mira la caseta si tiene fotos sin subir. */
export const SUBIR_FOTOS_INTERVALO_MS = 60_000;

/**
 * Cuántos días guarda la nube la imagen antes de borrarla.
 *
 * Siete cubre un fin de semana largo y un teléfono que se quedó sin batería.
 * Pasado eso la foto solo existe en la caseta y hay que pedírsela a la
 * administración — que es la regla aceptada al elegir buzón en vez de archivo.
 *
 * Es una constante y no una columna por condominio, al revés que
 * `retencionFotosDias`: aquel plazo lo pide la ley y cada administración puede
 * querer el suyo; este es una decisión de operación de Texelos sobre su propio
 * disco. El día que alguien pida cambiarlo, se promueve a columna.
 */
export const BUZON_FOTOS_DIAS = 7;

/**
 * Lo más grande que la nube acepta como una foto.
 *
 * Un rostro pesa unos 170 KB; el mismo tope que ya usa la subida del kiosco.
 * Existe porque el cuerpo llega en crudo y sin límite una caseta comprometida
 * —o con un error— podría llenar el disco de la nube con una sola petición.
 */
export const FOTO_MAX_BYTES = 8 * 1024 * 1024;
